import { useQuery } from "@tanstack/react-query";
import { Icon } from "./Icon";
import { api, type TimelineMessage } from "@/lib/api";
import { formatBytes } from "@/lib/media";
import { VoicePlayer } from "./VoicePlayer";

/**
 * Mídia na bolha (docs/05 §3.2 + 06): imagem inline com raio de bolha,
 * vídeo com player + poster, áudio/voz com player nativo, documento com
 * ícone + nome + tamanho. A URL vem da prévia local (bolha otimista) ou de
 * `GET /api/media/:id` (metadados + URL assinada), buscada só quando a
 * mídia está `ready` (sem requests especulativos).
 */

export interface LocalMedia {
  url: string;
  kind: "image" | "video" | "audio" | "voice" | "document" | "sticker";
  name?: string;
  size?: number;
  mime?: string;
  /** Duração medida no gravador (bolha otimista toca o blob local). */
  durationMs?: number | null;
}

function useMediaUrl(message: TimelineMessage): { url: string | null; fileName: string | null; mime: string | null } {
  const enabled = Boolean(message.mediaId) && message.mediaStatus === "ready";
  const query = useQuery({
    queryKey: ["media", message.mediaId],
    queryFn: () => api.media(message.mediaId as string),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
  return { url: query.data?.url ?? null, fileName: query.data?.fileName ?? null, mime: query.data?.mimeType ?? null };
}

export function MessageMedia({
  message,
  local,
  onOpenPhoto,
}: {
  message: TimelineMessage;
  local?: LocalMedia | null;
  /** Abre a foto em tela cheia; ausente em bolha otimista (ainda sem id). */
  onOpenPhoto?: () => void;
}) {
  const remote = useMediaUrl(message);
  const kind = local?.kind ?? message.type;
  const url = local?.url ?? remote.url;
  const caption = message.type === "document" ? null : message.body;
  const fileName = local?.name ?? remote.fileName ?? message.body?.trim() ?? "Anexo";
  const sizeLabel =
    local?.size !== undefined
      ? formatBytes(local.size)
      : remote.mime ?? local?.mime ?? message.type;
  // Duração: otimista local primeiro (`durationMs` do outbox), depois a do
  // servidor. O campo do outbox não está no DTO, dai o cast só nele.
  const serverDurationMs =
    message.mediaDurationMs ?? (message as { durationMs?: number }).durationMs ?? null;

  if (kind === "image") {
    return (
      <div className="bubble-media">
        {url ? (
          <img
            src={url}
            alt={caption ?? fileName}
            loading="lazy"
            className={onOpenPhoto ? "is-clickable" : undefined}
            onClick={onOpenPhoto}
          />
        ) : (
          <div className="bubble-media-ph" role="img" aria-label="Imagem indisponível">
            <span className="ph-icon">
              <Icon name="image" size={24} />
            </span>
            Imagem
          </div>
        )}
        {caption ? <div className="bubble-media-caption">{caption}</div> : null}
      </div>
    );
  }

  if (kind === "video") {
    return (
      <div className="bubble-media">
        {url ? (
          <video src={url} controls preload="metadata" playsInline aria-label={caption ?? fileName} />
        ) : (
          <div className="bubble-media-ph" role="img" aria-label="Vídeo indisponível">
            <span className="ph-icon">
              <Icon name="video" size={24} />
            </span>
            Vídeo
          </div>
        )}
        {caption ? <div className="bubble-media-caption">{caption}</div> : null}
      </div>
    );
  }

  if (kind === "audio" || kind === "voice") {
    const durationMs = local?.durationMs ?? serverDurationMs;
    // Sem URL reproduzível (sem prévia local e `media.status` não `ready`):
    // um estado único — o `VoicePlayer` sem `url` rende só ícone + rótulo +
    // duração. A legenda placeholder do seed ("Mensagem de voz"/nome igual ao
    // arquivo) é suprimida para não duplicar; legenda real e distinta segue.
    if (!url) {
      const rawCaption = caption?.trim() ?? "";
      const normalized = rawCaption.toLowerCase();
      const isPlaceholder =
        !rawCaption ||
        normalized === "mensagem de voz" ||
        normalized === "áudio" ||
        normalized === "audio" ||
        rawCaption === fileName;
      return (
        <div className="bubble-audio-col">
          <VoicePlayer
            url={null}
            seed={message.id}
            kind={kind}
            fileName={null}
            durationMs={durationMs}
            label="áudio"
          />
          {!isPlaceholder ? <div className="bubble-media-caption">{caption}</div> : null}
        </div>
      );
    }
    return (
      <div className="bubble-audio-col">
        <VoicePlayer
          url={url}
          seed={message.id}
          kind={kind}
          /* Sem nome real o player mostra "Áudio" — melhor que "Anexo" ou o
             hash do provider. */
          fileName={local?.name ?? remote.fileName ?? null}
          durationMs={durationMs}
          waveform={message.waveform ?? null}
          label={kind === "voice" ? "mensagem de voz" : `áudio ${fileName}`}
        />
        {caption ? <div className="bubble-media-caption">{caption}</div> : null}
      </div>
    );
  }

  if (kind === "sticker") {
    // O .webp ja e baixado e servido pelo mesmo caminho das outras midias — o
    // que faltava era usar a URL em vez do emoji de reserva, que ficava ate
    // para figurinha pronta. Sem fundo de bolha: quem cuida disso e a classe
    // `.bubble-sticker`, aplicada no `Bubble`.
    return (
      <div className="bubble-media">
        {url ? (
          <img className="sticker-img" src={url} alt={caption?.trim() || "Figurinha"} loading="lazy" draggable={false} />
        ) : (
          <div className="sticker-ph" role="img" aria-label="Figurinha">
            🙂
          </div>
        )}
      </div>
    );
  }

  if (kind === "document" || message.type === "document") {
    const inner = (
      <>
        <span className="doc-icon">
          <Icon name="document" size={20} />
        </span>
        <span className="doc-name">
          {fileName}
          <span className="doc-meta"> · {sizeLabel}</span>
        </span>
      </>
    );
    return url ? (
      <a className="bubble-doc" href={url} target="_blank" rel="noreferrer" aria-label={`Baixar ${fileName}`}>
        {inner}
      </a>
    ) : (
      <div className="bubble-doc" role="status" aria-label="Documento indisponível">
        {inner}
      </div>
    );
  }

  if (message.type === "location" || message.type === "contact") {
    return (
      <div className="media-pill">
        <span className="ic">
          <Icon name={message.type === "location" ? "location" : "contact"} size={20} />
        </span>
        {/* `??` nao cobre body = "" (o GOWS manda vazio nesses tipos) — sem o
            `||` a pilula saia so com o icone, sem rotulo nenhum. */}
        <span>{message.body?.trim() || (message.type === "location" ? "Localização" : "Contato")}</span>
      </div>
    );
  }

  return <span className="bubble-body">{message.body}</span>;
}
